import { useEffect, useMemo, useRef, useState } from 'react'

import { LngLatBounds, type Map as MLMap, type GeoJSONSource } from 'maplibre-gl'
import { useRoadStore } from '@/lib/roads/store'
import { mapInstance } from '@/lib/roads/userLocation'
import { resolveDetailedGeometry } from '@/lib/roads/api'
import {
  projectOnPolyline,
  excludedSegment,
  trimActive,
  polylineMiles,
  junctionSnapCandidates,
  type Coord,
  type RoadTrim,
  type JunctionSnap,
} from '@/lib/roads/trim'

const SOURCE_ID = 'trim-excluded-src'
const LINE_LAYER = 'trim-excluded-line'
// Screen-space snap radius in px: a handle within this distance of a known
// junction magnetizes to it.
const SNAP_PX = 24

// Coordinate at a fractional position (0..1) along the polyline.
function coordAtPos(coords: Coord[], pos: number): Coord {
  if (coords.length === 0) return [0, 0]
  if (coords.length === 1) return coords[0]
  const total = polylineMetersTotal(coords)
  if (total <= 0) return coords[Math.round(pos * (coords.length - 1))]
  const target = Math.max(0, Math.min(1, pos)) * total
  let cum = 0
  for (let i = 1; i < coords.length; i++) {
    const seg = distM(coords[i - 1], coords[i])
    if (cum + seg >= target) {
      const t = seg > 0 ? (target - cum) / seg : 0
      return [
        coords[i - 1][0] + t * (coords[i][0] - coords[i - 1][0]),
        coords[i - 1][1] + t * (coords[i][1] - coords[i - 1][1]),
      ]
    }
    cum += seg
  }
  return coords[coords.length - 1]
}

// Coordinates along the polyline between fractional positions a..b (the
// kept span of a trimmed road), used to frame the camera on trim open.
function keptCoordSpan(coords: Coord[], a: number, b: number): Coord[] {
  if (coords.length < 2) return coords
  if (a <= 0.001 && b >= 0.999) return coords
  const total = polylineMetersTotal(coords)
  const out: Coord[] = [coordAtPos(coords, a)]
  if (total > 0) {
    const startM = a * total
    const endM = b * total
    let cum = 0
    for (let i = 1; i < coords.length; i++) {
      const s0 = cum
      cum += distM(coords[i - 1], coords[i])
      if (s0 > startM && cum < endM) out.push(coords[i])
    }
  }
  out.push(coordAtPos(coords, b))
  return out
}

const EARTH_M = 6371000
const toRad = (d: number) => (d * Math.PI) / 180
function distM(a: Coord, b: Coord): number {
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2
  return EARTH_M * 2 * Math.atan2(Math.sqrt(Math.min(1, h)), Math.sqrt(1 - h))
}
function polylineMetersTotal(coords: Coord[]): number {
  let m = 0
  for (let i = 1; i < coords.length; i++) m += distM(coords[i - 1], coords[i])
  return m
}

export default function TrimOverlay() {
  // trimmingRoadId is the trim KEY of the visit being edited: the plain road
  // id for its first pass, "id#2"/"id#3"… for later visits.
  const trimmingRoadId = useRoadStore((s) => s.trimmingRoadId)
  const setTrimmingRoad = useRoadStore((s) => s.setTrimmingRoad)
  const roads = useRoadStore((s) => s.roads)
  const routeIds = useRoadStore((s) => s.routeIds)
  const routeTrims = useRoadStore((s) => s.routeTrims)
  const setTrim = useRoadStore((s) => s.setTrim)
  const trimDrag = useRoadStore((s) => s.trimDrag)
  const setTrimDrag = useRoadStore((s) => s.setTrimDrag)
  const routeIdsKey = routeIds.join(',')

  // Dense polylines for the trimmed road + the other route roads. The road
  // records carry only coarse link waypoints; the map's resolved geometry
  // (OSRM-snapped) is what dashes and handles must follow. resolveDetailed-
  // Geometry serves repeated calls from its in-memory cache, so route roads
  // already on screen resolve instantly.
  const [geomMap, setGeomMap] = useState<Map<string, Coord[]>>(new Map())
  useEffect(() => {
    if (!trimmingRoadId) {
      setGeomMap(new Map())
      return
    }
    let cancelled = false
    const store = useRoadStore.getState()
    const baseId = (trimmingRoadId ?? '').split('#')[0]
    const ids = [baseId, ...routeIds.filter((id) => id !== baseId)]
    void (async () => {
      const next = new Map<string, Coord[]>()
      for (const id of ids) {
        const r = store.roads.find((x) => x.id === id)
        if (!r || r.geometry?.type !== 'LineString') continue
        const c = await resolveDetailedGeometry(r)
        if (cancelled) return
        if (c && c.length >= 2) next.set(id, c as Coord[])
      }
      if (!cancelled) setGeomMap(next)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmingRoadId, routeIdsKey])

  const road = useMemo(
    () => roads.find((r) => r.id === (trimmingRoadId ?? '').split('#')[0]) ?? null,
    [roads, trimmingRoadId],
  )
  const coords: Coord[] = useMemo(() => {
    const baseId = (trimmingRoadId ?? '').split('#')[0]
    const dense = baseId ? geomMap.get(baseId) : undefined
    if (dense) return dense
    const g = road?.geometry
    if (!g || g.type !== 'LineString') return []
    return (g.coordinates as Coord[]) ?? []
  }, [geomMap, trimmingRoadId, road])

  // Junction snap candidates against the OTHER roads in the route. Recomputed
  // only when the trimmed road or the route membership changes.
  const snaps: JunctionSnap[] = useMemo(() => {
    if (!trimmingRoadId || coords.length < 2) return []
    const baseId = (trimmingRoadId ?? '').split('#')[0]
    const others = routeIds
      .filter((id) => id !== baseId)
      .map((id) => {
        const c = geomMap.get(id)
        return c ? { roadId: id, coords: c } : null
      })
      .filter((x): x is { roadId: string; coords: Coord[] } => !!x)
    return junctionSnapCandidates(coords, others)
  }, [trimmingRoadId, routeIds, geomMap, coords])

  const snapNameFor = (otherRoadId: string | null | undefined) =>
    otherRoadId ? (roads.find((r) => r.id === otherRoadId)?.name ?? null) : null

  // Live drag positions override the committed ends while a handle is held.
  // A snapped drag pins the cut exactly at the junction coord. Handles can't
  // cross: the dragged end stops a hair short of the opposite cut.
  const committed = routeTrims[trimmingRoadId ?? ''] ?? {}
  const isRepeatVisit = !!trimmingRoadId && trimmingRoadId.includes('#')
  const trim: RoadTrim = useMemo(() => {
    const active = trimDrag?.roadId === trimmingRoadId ? trimDrag : null
    const MIN_GAP = 0.02
    let head: RoadTrim['head'] = committed.head ?? null
    let tail: RoadTrim['tail'] = committed.tail ?? null
    if (active) {
      const snapCoord = (active.snapCoord ?? null) as Coord | null
      if (active.headPos != null) {
        const capped =
          tail ? Math.min(active.headPos, (tail.pos ?? 1) - MIN_GAP) : active.headPos
        head = { pos: capped, coord: snapCoord ?? coordAtPos(coords, capped) }
      }
      if (active.tailPos != null) {
        const floored =
          head ? Math.max(active.tailPos, (head.pos ?? 0) + MIN_GAP) : active.tailPos
        tail = { pos: floored, coord: snapCoord ?? coordAtPos(coords, floored) }
      }
    }
    // reversed rides along so a drag commit never wipes the direction flag.
    return { head, tail, reversed: committed.reversed }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDrag, trimmingRoadId, committed, coords])

  // Commit on release: while a handle is dragged we keep the live trim in a
  // ref; when the drag clears, merge the dragged end into the committed trim
  // (the other end keeps its committed value). Clearing the ref first (the
  // handle's Escape path) cancels instead of committing.
  const lastLiveTrim = useRef<{ roadId: string; trim: RoadTrim } | null>(null)
  useEffect(() => {
    if (trimDrag && trimDrag.roadId === trimmingRoadId) {
      lastLiveTrim.current = { roadId: trimDrag.roadId, trim }
      return
    }
    if (!trimDrag && lastLiveTrim.current) {
      const { roadId, trim: live } = lastLiveTrim.current
      lastLiveTrim.current = null
      if (live.head || live.tail) setTrim(roadId, live)
    }
    // (roadId here is already the occurrence trim key — the store's setTrim
    // stores trims under exactly this key, so repeat visits stay separate.)
  }, [trimDrag, trimmingRoadId, trim, setTrim])

  const map = mapInstance.current

  // On trim open (and when the geometry finishes resolving), animate the
  // camera to the road — or, if it already has committed cuts, to just the
  // kept span — so both trim bubbles are visible before the user drags.
  const framedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const m: MLMap | null = mapInstance.current
    if (!m || !trimmingRoadId || coords.length < 2) return
    if (framedKeyRef.current === trimmingRoadId) return
    framedKeyRef.current = trimmingRoadId
    const committedTrim = routeTrims[trimmingRoadId] ?? {}
    let span: Coord[] = coords
    if (committedTrim.head != null || committedTrim.tail != null) {
      const a = committedTrim.head?.pos ?? 0
      const b = committedTrim.tail?.pos ?? 1
      span = keptCoordSpan(coords, a, b)
    }
    const bounds = new LngLatBounds(span[0] as [number, number], span[0] as [number, number])
    for (const c of span) bounds.extend(c as [number, number])
    // Keep the framed road clear of the route tray wherever it actually sits:
    // docked right on desktop -> pad the RIGHT side so no part of the road
    // (or its bubbles) lands in the tray's column; full-width at the bottom
    // on mobile -> pad the bottom band instead.
    // Match the road-selection camera in MapCanvas exactly: same measured
    // top-bar clearance, same side padding, same maxZoom cap (14) — so
    // entering trim frames the road identically to selecting it.
    const small = window.innerWidth < 768
    const topbar = document.getElementById('rr-topbar')
    const topbarPad = topbar ? Math.ceil(topbar.getBoundingClientRect().height) : 80
    const adminPad =
      document.querySelector('[data-admin-portal="true"]') != null ? 340 : 120
    let bottomPad = 100
    if (small) {
      for (const sel of ['[data-route-tray]', '[data-trim-controls]']) {
        const el = document.querySelector(sel)
        if (!el) continue
        const r = el.getBoundingClientRect()
        if (r.height <= 0 || r.top >= window.innerHeight) continue
        bottomPad = Math.max(bottomPad, window.innerHeight - r.top + 16)
      }
      bottomPad = Math.min(bottomPad, Math.round(window.innerHeight * 0.55))
    }
    const panelPad = small
      ? { top: topbarPad + 24, bottom: bottomPad, left: 60, right: 60 }
      : { top: topbarPad + 24, bottom: 100, left: adminPad, right: 460 }
    // fitBounds + maxZoom silently DROPS the padding when the natural fit
    // zoom exceeds the cap: it centers on the raw viewport center, dropping
    // the road right onto the route tray. Compute the camera explicitly so
    // the padded center is kept even when the zoom is capped.
    const fit = m.cameraForBounds(bounds, { padding: panelPad })
    const zoom = Math.min(fit.zoom ?? 16, 14)
    m.easeTo({ center: fit.center, zoom, duration: 800, essential: true })
  }, [trimmingRoadId, coords, routeTrims])

  // Push the excluded head/tail segments into the map source. Runs on every
  // trim change (including live drags) so the dashed preview tracks the cut
  // along the real polyline.
  useEffect(() => {
    const m: MLMap | null = mapInstance.current
    if (!m || coords.length < 2) return
    if (!m.getSource(SOURCE_ID)) {
      m.addSource(SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      m.addLayer({
        id: LINE_LAYER,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#f59e0b',
          'line-width': 4,
          'line-dasharray': [1.5, 1.5],
        },
      })
    }
    const src = m.getSource(SOURCE_ID) as GeoJSONSource | undefined
    if (!src) return
    // While idle, committed trims are rendered persistently by MapCanvas's
    // rr-road-trimmed layer — this amber preview only mirrors LIVE drags so
    // the two never double-draw the same section.
    if (!trimDrag) {
      src.setData({ type: 'FeatureCollection', features: [] })
      return
    }
    const features: GeoJSON.Feature[] = []
    const headSeg = excludedSegment(coords, trim, 'head')
    if (headSeg.length >= 2)
      features.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: headSeg },
      })
    const tailSeg = excludedSegment(coords, trim, 'tail')
    if (tailSeg.length >= 2)
      features.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: tailSeg },
      })
    src.setData({ type: 'FeatureCollection', features })
  }, [trim, coords, map])

  // Remove the map layer when trim mode ends.
  useEffect(() => {
    return () => {
      const m: MLMap | null = mapInstance.current
      if (!m) return
      try {
        if (m.getLayer(LINE_LAYER)) m.removeLayer(LINE_LAYER)
        if (m.getSource(SOURCE_ID)) m.removeSource(SOURCE_ID)
      } catch {
        /* map already torn down */
      }
    }
  }, [])

  if (!road || coords.length < 2) {
    return (
      <div className="fixed left-1/2 top-20 z-[60] -translate-x-1/2 rounded-xl border border-white/10 bg-[#0C0C0E] px-5 py-3 text-sm text-white/80">
        {road ? 'Preparing road geometry for trimming…' : 'This road has no detailed geometry to trim yet.'}
        <button
          onClick={() => setTrimmingRoad(null)}
          className="ml-3 rounded-md border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/10"
        >
          Close
        </button>
      </div>
    )
  }

  const headCoord = trim.head?.coord ?? coords[0]
  const tailCoord = trim.tail?.coord ?? coords[coords.length - 1]
  const excludedMiles =
    polylineMiles(excludedSegment(coords, trim, 'head')) +
    polylineMiles(excludedSegment(coords, trim, 'tail'))

  const draggingThis = trimDrag?.roadId === trimmingRoadId
  const snapName = snapNameFor(draggingThis ? trimDrag?.snapRoadId : null)

  return (
    <>
      <TrimHandle
        map={map}
        coord={headCoord}
        pos={trim.head?.pos ?? 0}
        label={trim.reversed ? 'End' : 'Start'}
        roadId={trimmingRoadId ?? road.id}
        coords={coords}
        end="head"
        snaps={snaps}
        onDrag={setTrimDrag}
        onCancel={() => {
          lastLiveTrim.current = null
          setTrimDrag(null)
        }}
      />
      <TrimHandle
        map={map}
        coord={tailCoord}
        pos={trim.tail?.pos ?? 1}
        label={trim.reversed ? 'Start' : 'End'}
        roadId={trimmingRoadId ?? road.id}
        coords={coords}
        end="tail"
        snaps={snaps}
        onDrag={setTrimDrag}
        onCancel={() => {
          lastLiveTrim.current = null
          setTrimDrag(null)
        }}
      />
      {/* Trim control sits ABOVE the route tray (z-60 > tray z-40), anchored
          over it on desktop, above it on mobile. */}
      <div data-trim-controls className="pointer-events-auto fixed inset-x-3 bottom-3 z-[60] rounded-xl border border-amber-400 bg-[#0C0C0E] px-5 py-3 text-sm text-white shadow-lg md:inset-x-auto md:left-1/2 md:bottom-3 md:w-auto md:-translate-x-1/2">
        <div className="flex items-center gap-4">
          <div>
            <div className="font-medium text-amber-400">
              Trim {road.name}
              {isRepeatVisit && (
                <span className="ml-1.5 text-[10px] font-mono uppercase tracking-widest text-white/50">
                  {trimmingRoadId?.split('#')[1]} visit
                </span>
              )}
            </div>
            <div className="text-xs text-white/60">
              {snapName ? (
                <span className="text-emerald-300">Snapped to {snapName}</span>
              ) : (
                'Drag the handles to exclude road sections'
              )}
            </div>
            {excludedMiles > 0.05 && (
              <div className="text-xs text-amber-400">{excludedMiles.toFixed(1)} mi excluded</div>
            )}
          </div>
          <button
            onClick={() => {
              const nextReversed = !committed.reversed
              const hasCuts = !!(committed.head || committed.tail)
              if (!hasCuts && !nextReversed) setTrim(trimmingRoadId ?? road.id, null)
              else setTrim(trimmingRoadId ?? road.id, { ...committed, reversed: nextReversed })
            }}
            className={`rounded-md border px-2.5 py-1 text-xs ${
              committed.reversed
                ? 'border-amber-400/50 bg-amber-400/10 text-amber-300'
                : 'border-white/15 text-white/70 hover:bg-white/10'
            }`}
            title="Flip which end of the road the ride starts from"
          >
            {committed.reversed ? 'Reversed' : 'Reverse'}
          </button>
          {(() => {
            const canReset = !!(committed.head || committed.tail) || !!committed.reversed
            return (
              <button
                onClick={() => canReset && setTrim(trimmingRoadId ?? road.id, null)}
                disabled={!canReset}
                className={`rounded-md border px-2.5 py-1 text-xs ${
                  canReset
                    ? 'border-white/15 text-white/70 hover:bg-white/10'
                    : 'cursor-not-allowed border-white/10 text-white/30'
                }`}
              >
                Reset
              </button>
            )
          })()}
          <button
            onClick={() => setTrimmingRoad(null)}
            className="rounded-md bg-amber-400 px-3 py-1 text-xs font-semibold text-black hover:bg-amber-300"
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}

interface DragPayload {
  roadId: string
  headPos: number | null
  tailPos: number | null
  snapCoord?: Coord | null
  snapRoadId?: string | null
}

// Draggable round handle anchored to a map coordinate; dragging projects the
// pointer onto the road polyline and reports the fractional position live.
// When the pointer lands near a junction with another route road, the drag
// magnetizes to that junction and reports its exact coordinate.
//
// The dot's position lives OUTSIDE React's render loop: it is written as a
// transform on every map move / pointer move, and while dragging it is set to
// the projection of the projected cut coordinate — so the dot hugs the road
// polyline frame-by-frame instead of jumping between renders.
function TrimHandle({
  map,
  coord,
  pos,
  label,
  roadId,
  coords,
  end,
  snaps,
  onDrag,
  onCancel,
}: {
  map: MLMap | null
  coord: Coord
  pos: number
  label: string
  roadId: string
  coords: Coord[]
  end: 'head' | 'tail'
  snaps: JunctionSnap[]
  onDrag: (d: DragPayload | null) => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const dotRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const snappedRef = useRef(false)
  const snapsRef = useRef(snaps)
  snapsRef.current = snaps
  const coordsRef = useRef(coords)
  coordsRef.current = coords
  const posRef = useRef<{ x: number; y: number } | null>(null)

  const applyPos = () => {
    const el = ref.current
    const p = posRef.current
    if (!el) return
    if (!p) {
      el.style.display = 'none'
      return
    }
    el.style.display = ''
    el.style.transform = `translate(${p.x - 22}px, ${p.y - 22}px)`
  }

  const setSnapped = (on: boolean) => {
    if (snappedRef.current === on) return
    snappedRef.current = on
    const dot = dotRef.current
    if (dot) dot.style.transform = on ? 'scale(1.6)' : 'scale(1)'
  }

  // Keep the handle glued to its map coordinate on every pan/zoom — direct
  // DOM write, no re-render, no jump.
  useEffect(() => {
    if (!map) return
    const update = () => {
      try {
        posRef.current = map.project(coord as [number, number]) as { x: number; y: number }
      } catch {
        posRef.current = null
      }
      applyPos()
    }
    update()
    map.on('move', update)
    map.on('zoom', update)
    return () => {
      map.off('move', update)
      map.off('zoom', update)
    }
  }, [map, coord])

  // Start dragging: listeners attach HERE (not in an effect keyed on render
  // state), so a press always grabs the handle regardless of render timing.
  const onPointerDown = (e: React.PointerEvent) => {
    if (!map || draggingRef.current) return
    e.preventDefault()
    e.stopPropagation()
    draggingRef.current = true
    document.body.style.cursor = 'grabbing'

    const emit = (ev: PointerEvent) => {
      const c = coordsRef.current
      if (c.length < 2) return
      const rect = map.getContainer().getBoundingClientRect()
      const ll = map.unproject([ev.clientX - rect.left, ev.clientY - rect.top])
      const proj = projectOnPolyline([ll.lng, ll.lat], c)
      // Magnetize: within SNAP_PX of a junction candidate, pin to it.
      let snap: { snap: JunctionSnap; pos: number } | null = null
      let bestPx = SNAP_PX
      for (const cand of snapsRef.current) {
        let cp: { x: number; y: number }
        try {
          cp = map.project(cand.coord as [number, number]) as { x: number; y: number }
        } catch {
          continue
        }
        const d = Math.hypot(cp.x + rect.left - ev.clientX, cp.y + rect.top - ev.clientY)
        if (d < bestPx) {
          bestPx = d
          snap = { snap: cand, pos: projectOnPolyline(cand.coord, c).pos }
        }
      }
      setSnapped(!!snap)
      const cutCoord: Coord = snap ? snap.snap.coord : proj.coord
      const cutPos = snap ? snap.pos : proj.pos
      // The dot follows the polyline itself: paint the projected cut point.
      try {
        posRef.current = map.project(cutCoord as [number, number]) as { x: number; y: number }
      } catch {
        /* keep last position */
      }
      applyPos()
      onDrag({
        roadId,
        headPos: end === 'head' ? cutPos : null,
        tailPos: end === 'tail' ? cutPos : null,
        snapCoord: snap ? snap.snap.coord : null,
        snapRoadId: snap ? snap.snap.otherRoadId : null,
      })
    }
    const onMoveEvt = (ev: PointerEvent) => {
      if (ev.pointerType === 'mouse' && ev.buttons === 0) {
        finish()
        return
      }
      ev.preventDefault()
      emit(ev)
    }
    const finish = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      setSnapped(false)
      document.body.style.cursor = ''
      window.removeEventListener('pointermove', onMoveEvt)
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
      onDrag(null)
    }
    window.addEventListener('pointermove', onMoveEvt, { passive: false })
    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
    // Emit once immediately so a tap-without-drag still registers the cut.
    emit(e.nativeEvent)
  }

  // Keyboard parity: arrow keys walk the cut along the road (Shift = fine
  // steps), Enter/Escape end editing. Escape mid-drag cancels the live drag.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (draggingRef.current) {
        draggingRef.current = false
        setSnapped(false)
        document.body.style.cursor = ''
      }
      onCancel()
      return
    }
    const step = (e.shiftKey ? 0.002 : 0.01) * 50
    let dir = 0
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') dir = -step
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') dir = step
    if (!dir) return
    e.preventDefault()
    const next = Math.max(0, Math.min(1, pos + dir))
    onDrag({
      roadId,
      headPos: end === 'head' ? next : null,
      tailPos: end === 'tail' ? next : null,
      snapCoord: null,
      snapRoadId: null,
    })
    window.setTimeout(() => onDrag(null), 120)
  }

  return (
    <div
      ref={ref}
      role="slider"
      aria-label={`Trim ${label} handle`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos * 100)}
      tabIndex={0}
      className="fixed left-0 top-0 z-20 flex h-11 w-11 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <div
        ref={dotRef}
        className="h-4 w-4 rounded-full border-2 border-amber-300 bg-black shadow-[0_0_0_2px_rgba(0,0,0,0.5)] transition-transform duration-150 hover:scale-125"
      />
      <span className="pointer-events-none absolute -top-6 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
        {label}
      </span>
    </div>
  )
}
